import { useFormContext, Controller } from 'react-hook-form';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControlLabelProps,
  Box,
  Tooltip,
  FormHelperText,
} from '@mui/material';
import Iconify from '../Iconify';
import { IconifyIconEnum } from 'src/@types/icon';
import useLocales from 'src/hooks/useLocales';
import { ExportTypeCustomSetting, ExportTypeLabel } from 'src/@types/export';

interface RHFCheckboxProps extends Omit<FormControlLabelProps, 'control'> {
  name: string;
}

export function RHFCheckbox({ name, ...other }: RHFCheckboxProps) {
  const { control } = useFormContext();

  return (
    <FormControlLabel
      control={
        <Controller
          name={name}
          control={control}
          render={({ field }) => <Checkbox {...field} checked={field.value} />}
        />
      }
      {...other}
    />
  );
}

interface RHFMultiCheckboxProps extends Omit<FormControlLabelProps, 'control' | 'label'> {
  name: string;
  style?: React.CSSProperties;
  numberOfColumns?: number;
  options: {
    label: string;
    value: any;
    tooltip?: string;
  }[];
}

export function RHFMultiCheckbox({
  name,
  options,
  numberOfColumns = 1,
  style,
  ...other
}: RHFMultiCheckboxProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const onSelected = (option: string) =>
          field.value.includes(option)
            ? field.value.filter((value: string) => value !== option)
            : [...field.value, option];

        return (
          <FormGroup style={style}>
            <Box
              sx={{
                columnCount: numberOfColumns,
              }}
            >
              {options.map((option) => (
                <Box
                  key={option.value}
                  sx={{
                    display: 'flex',
                    width: '100%',
                  }}
                >
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={field.value.includes(option.value)}
                        onChange={() => field.onChange(onSelected(option.value))}
                      />
                    }
                    label={option.label}
                    {...other}
                  />
                  {option.tooltip && (
                    <Tooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={5000}
                      title={option.tooltip}
                      placement="top"
                    >
                      <Box component="span" display="flex" alignItems="center">
                        <Iconify icon={IconifyIconEnum.Info} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
          </FormGroup>
        );
      }}
    />
  );
}

interface RHFExportMultiCheckboxProps extends Omit<FormControlLabelProps, 'control' | 'label'> {
  name: string;
  style?: React.CSSProperties;
  exportType?: ExportTypeLabel;
  numberOfColumns?: number;
  options: ExportTypeCustomSetting[];
}

export function RHFExportMultiCheckbox({
  name,
  options,
  exportType,
  numberOfColumns = 1,
  style,
  ...other
}: RHFExportMultiCheckboxProps) {
  const { control, setValue } = useFormContext();
  const { translateUnsafe } = useLocales();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const onSelected = (option: ExportTypeCustomSetting) => {
          const values = option.mappedValues ?? [option.label];

          const newValue = {
            ...field.value,
            [option.fieldGroup]: values.every((value) =>
              field.value[option.fieldGroup].includes(value)
            )
              ? field.value[option.fieldGroup].filter((value: string) => !values.includes(value))
              : [...field.value[option.fieldGroup], ...values],
          };

          return newValue;
        };

        return (
          <FormGroup style={style}>
            <Box
              sx={{
                columnCount: numberOfColumns,
              }}
            >
              {options.map((option) => (
                <Box
                  key={option.label}
                  sx={{
                    display: 'flex',
                    width: '100%',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(option.mappedValues ?? [option.label]).every((value) =>
                          field.value[option.fieldGroup].includes(value)
                        )}
                        onChange={() =>
                          setValue(name, onSelected(option), { shouldValidate: true })
                        }
                      />
                    }
                    label={translateUnsafe(
                      `export.form.customSettings.${exportType}.${option.label}`
                    )}
                    {...other}
                  />
                  {option.tooltip && (
                    <Tooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={5000}
                      title={translateUnsafe(`export.form.tooltips.${option.label}`)}
                      placement="top"
                    >
                      <Box component="span" display="flex" alignItems="center">
                        <Iconify icon={IconifyIconEnum.Info} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>

            {!!error && (
              <FormHelperText error style={{ marginLeft: 14 }}>
                {error.message}
              </FormHelperText>
            )}
          </FormGroup>
        );
      }}
    />
  );
}
